import { Component, OnInit, computed, inject, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ErrorRule } from '../models/error-rule';
import { DxDataGridComponent, DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { StakeholderService } from 'src/app/stakeholder/services/stakeholder.service';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { CellPreparedEvent, ColumnButtonClickEvent, RowDblClickEvent, RowPreparedEvent, ToolbarPreparingEvent } from 'devextreme/ui/data_grid';
import { Router } from '@angular/router';
import { ErrorRulesService } from '../services/error-rules.service';
import { GenericModalService } from 'src/app/shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { ButtonComponent } from '../../controls/components/basic/button/button.component';
import { ButtonPrimaryDirective } from '../../controls/directives/button-primary.directive';
import { ButtonSecondaryDirective } from '../../controls/directives/button-secondary.directive';

import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoRowDraggingModule, DxoSortingModule, DxoGroupPanelModule, DxiColumnModule, DxoLookupModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-error-rules-overview',
    templateUrl: './error-rules-overview.component.html',
    styleUrls: ['./error-rules-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, ButtonComponent, ButtonPrimaryDirective, ButtonSecondaryDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoRowDraggingModule, DxoSortingModule, DxoGroupPanelModule, DxiColumnModule, DxoLookupModule, BizDateColumnDirective, BizUserColumnDirective, DxiButtonModule]
})
export class ErrorRulesOverviewComponent implements OnInit {
  private router = inject(Router);
  private stakeholderService = inject(StakeholderService);
  private errorRulesService = inject(ErrorRulesService);
  private currentUserService = inject(CurrentUserService);
  private loadingPanelService = inject(LoadingPanelService);
  private modalService = inject(GenericModalService);
  private toastService = inject(ToastService);
  public bizGridFiltervalues = inject(BizGridFiltervalues);

  orderEditMode: boolean = false;
  allowUpdating: boolean = this.currentUserService.hasPermission('ErrorRules_Action_Edit');;
  allowAdding: boolean = this.currentUserService.hasPermission('ErrorRules_Action_Create')
  allowDeleting: boolean = this.currentUserService.hasPermission('ErrorRules_Action_Delete')

  datagrid = viewChild.required<DxDataGridComponent>('errorrules');
  errorRules = signal<ErrorRule[]>([]);
  stakeholders = signal<Stakeholder[]>([]);

  defaultRuleIndex = computed(() => {
    const noOfRules = this.errorRules()?.length ?? 0;
    return noOfRules > 0 ? noOfRules-1 : 0
  });

  defaultRule = computed(() => {
    return this.errorRules()?.slice(-1)[0];
  });

  async ngOnInit(): Promise<void> {
    await this.refresh()
  }

  async refresh(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading...');
      const [stakeholders, errorRules] = await Promise.all([
        this.stakeholderService.getStakeholders(),
        this.errorRulesService.getErrorRules()
      ]);
      this.errorRules.set(errorRules);
      this.stakeholders.set(stakeholders);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  onToolbarPreparing = ($event: ToolbarPreparingEvent) => {
    const item = $event.toolbarOptions.items.find(e => e.name === 'addRowButton');
    if (item) {
      item.options.onClick = () => this.onAddErrorRule(0);
    }
  };

  onAddErrorRule(id: number) {
    const errorRuleId = id;
    this.router.navigate(['error/error-rules/details', {errorRuleId}]);
  }

  onSetFallbackClick() {
    const errorRuleId = this.defaultRule().Id;
    this.router.navigate(['error/error-rules/details', {errorRuleId}]);
  }

  onEditOrderClick() {
    this.orderEditMode = true;
    this.datagrid().instance.clearFilter()
    this.datagrid().instance.option('searchPanel', {
      visible: false,
    });
    this.datagrid().instance.option('headerFilter', {
      visible: false
    });
  }

  onSaveOrderClick() {
    this.onSaveOrderClickAsync();
  }

  async onSaveOrderClickAsync(): Promise<void> {
      try {
        this.loadingPanelService.showLoadingPanelDirect('Saving...');
        const orderedIds = this.errorRules().map(rule => rule.Id);
        await this.errorRulesService.saveSortOrder(orderedIds);
        const errorRules =  await this.errorRulesService.getErrorRules()
        this.errorRules.set(errorRules);
        this.orderEditMode = false;
      }
      catch {
        this.toastService.showToast('Failed to save rule order', ToastType.error);
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
  };

  onCandelOrderEditClick() {
      this.refresh();
      this.orderEditMode = false;
      this.datagrid().instance.option('searchPanel', {
        visible: true,
      });
      this.datagrid().instance.option('headerFilter', {
        visible: true
      });
  }

  onReorder = (e: Parameters<DxDataGridTypes.RowDragging['onReorder']>[0]) => {
    e.promise = this.onReorderAsync(e);
  };

  async onReorderAsync(e: Parameters<DxDataGridTypes.RowDragging['onReorder']>[0]): Promise<void> {
    const visibleRows = e.component.getVisibleRows();
    const workingArray = this.errorRules().slice();
    const toIndex = workingArray.findIndex((item) => item.Id === visibleRows[e.toIndex].data.Id);
    const fromIndex = workingArray.findIndex((item) => item.Id === e.itemData.Id);
    this.moveItem(workingArray, fromIndex, toIndex);
    this.errorRules.set(workingArray)
    await e.component.refresh()
  }

  moveItem<T>(array: T[], indexFrom: number, indexTo: number): T[] {
    // Check if indexFrom and indexTo are within the bounds of the array
    if (indexFrom < 0 || indexFrom >= array.length || indexTo < 0 || indexTo >= array.length) {
        throw new Error('Index out of bounds');
    }

    // Remove the item at indexFrom and store it in a variable
    const item = array.splice(indexFrom, 1)[0];

    // Insert the item at indexTo
    array.splice(indexTo, 0, item);

    return array;
  }



  editErrorRule = (e: ColumnButtonClickEvent) => {
    const errorRule = e.row.data as ErrorRule;
    const errorRuleId = errorRule.Id;
    this.router.navigate(['error/error-rules/details', { errorRuleId }]);
  };

  deleteErrorRule = (e: ColumnButtonClickEvent) => {
      const errorRule: ErrorRule = e.row.data;
      this.delete(errorRule)
  };

  async delete(errorRule: ErrorRule): Promise<void> {
    try {
      await this.modalService.confirmDirect(`Delete ${errorRule.Name}`, 'Are you sure you want to delete this error rule?');
      try {
        await this.errorRulesService.deleteErrorRule(errorRule.Id);
        this.errorRules.set(this.errorRules().filter(e => e.Id !== errorRule.Id));
        this.toastService.showToast('Error rule deleted successfully.', ToastType.success);
      }
      catch {
        this.toastService.showToast('Failed to delete error rule.', ToastType.error);
      }
    }
    catch {
      // User cancelled
    }
  }

  onDragStart = (e: any) => {
    if (e.itemData.Id === this.defaultRule().Id) {
      e.cancel = true;
    }
  }

  onDragChange = (e: any) => {
    if (e.toIndex === this.defaultRuleIndex()) {
     e.cancel = true;
    }
  }

  onCellPrepared = (e: CellPreparedEvent) => {
    if (e.column.type === "drag" && e.data?.Id == this.defaultRule().Id) {
      e.cellElement.removeClass('dx-command-drag');
    }
  }

  onRowPrepared = (e: RowPreparedEvent) => {
    if (e.rowType === "data" && e.data?.Id == this.defaultRule().Id) {
      e.rowElement.addClass("default-rule-row");
      e.rowElement.removeClass("dx-row-alt");
    }
  }

  onRowDblClick = (e: RowDblClickEvent) => {
    if (this.allowUpdating) {
      const errorRule = e.data as ErrorRule;
      const errorRuleId = errorRule.Id;
      this.router.navigate(['error/error-rules/details', { errorRuleId }]);
    }
  }

  isDeleteVisible = (e: any) => {
    if (e.row.data.Id !== this.defaultRule().Id && this.allowDeleting) {
      return true;
    }
    return false;
  }

  getSortOrderDisplay = (e: any) => {
    if (e.Id === this.defaultRule().Id) {
      return ""
    }
    return e.SortOrder;
  }

}
